import { CALLING_CODE_TO_COUNTRY_MAP, COUNTRY_FORMATS } from './phone-number.constants';
import { ParsedPhone } from './phone-number.types';
import { formatPlainPhoneNumber, getNationalPhoneNumber } from './phone-number.utils';

const sortedCallingCodes = Object.keys(CALLING_CODE_TO_COUNTRY_MAP).sort((a, b) => b.length - a.length);

const resolveCallingCode = (countryCallingCode: string, isUs: boolean): string => {
  return isUs ? '' : countryCallingCode;
};

const createParsedPhone = (props: { countryCallingCode: string; nationalNumber: string }): ParsedPhone => {
  const { countryCallingCode, nationalNumber } = props;
  const country = CALLING_CODE_TO_COUNTRY_MAP[countryCallingCode] || 'UNKNOWN';
  const formatter = COUNTRY_FORMATS[country]?.formatter;
  const isNeedFormatting = nationalNumber.length > 0;
  const formatted = isNeedFormatting
    ? formatter(nationalNumber)
    : resolveCallingCode(countryCallingCode, country === 'US');
  const formattedNational = getNationalPhoneNumber(formatted, countryCallingCode);
  const formattedInternational = nationalNumber
    ? `+${countryCallingCode} ${formattedNational}`
    : `+${countryCallingCode}`;

  return {
    country,
    countryCallingCode,
    nationalNumber,
    formattedForInput: formatted,
    formattedNational,
    formattedInternational,
    formattedInternationalPlain: formatPlainPhoneNumber(formattedInternational),
    formattedNationalPlain: formatPlainPhoneNumber(formattedNational),
    e164: `+${countryCallingCode}${nationalNumber}`,
  };
};

export const parsePhoneNumber = (phone = ''): ParsedPhone => {
  if (phone.length <= 2) {
    return createParsedPhone({
      countryCallingCode: COUNTRY_FORMATS.US.countryCallingCode,
      nationalNumber: phone,
    });
  }
  const cleaned = phone.replace(/\D/g, '');
  const isInternational = phone.trim().startsWith('+') || cleaned.length > 10;

  if (!isInternational) {
    return createParsedPhone({
      countryCallingCode: COUNTRY_FORMATS.US.countryCallingCode,
      nationalNumber: cleaned,
    });
  }

  const countryCallingCode = sortedCallingCodes.find((code) => cleaned.startsWith(code));
  if (countryCallingCode) {
    const nationalNumber = cleaned.slice(countryCallingCode.length);
    return createParsedPhone({ countryCallingCode, nationalNumber });
  }

  return createParsedPhone({
    countryCallingCode: '',
    nationalNumber: cleaned,
  });
};
